import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { setIdleTimeInSeconds } from "../../Redux/AutoLogout/autoLogoutSlice";
import LoginFunction from "../../Functions/Login/Login";
import { clearAccess } from "../../Redux/Access/accessSlice";
import { clearUser } from "../../Redux/User/userSlice";
import { formatSecondsToMinutes } from "../../Utils/timeFunctions";

const IdleLogout: React.FC = () => {
  const dispatch = useDispatch();
  const idleTimeInSeconds = useSelector(
    (state: RootState) => state.autoLogout.idleTimeInSeconds
  );
  const sessionToken = useSelector((state: RootState) => state.user.token);
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [remaining, setRemaining] = useState<number>(0);
  const navigate = useNavigate();

  const { getRemainingTime, reset, pause, start } = useIdleTimer({
    timeout: idleTimeInSeconds ? idleTimeInSeconds * 1000 : undefined,
    throttle: 500,
    events: ["mousemove", "keydown", "scroll", "click"],
  });

  useEffect(() => {
    start();
  }, [idleTimeInSeconds, start]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);
    return () => {
      clearInterval(interval);
    };
  });

  useEffect(() => {
    if (remaining > 30) {
      if (document.title !== "Wiline Admin Portal") {
        document.title = "Wiline Admin Portal";
      }
    }
    if (remaining > 0 && remaining <= 30) {
      document.title = `${formatSecondsToMinutes(
        remaining
      )} until your session times out!`;
    }
    if (remaining === 30) {
      setIsModalVisible(true);
    }
    if (remaining === 1) {
      document.title = `Your session has expired.`;
      setIsModalVisible(false);
      LoginFunction.logout(accessToken ?? "", sessionToken ?? "");
      dispatch(clearAccess(null));
      dispatch(clearUser(null));
      dispatch(setIdleTimeInSeconds({ idleTimeInSeconds: undefined }));
      reset();
      pause();
      navigate("/");
    }
  }, [dispatch, navigate, pause, remaining, reset]);

  useEffect(() => {
    const handleMouseMove = () => {
      setIsModalVisible(false);
    };

    if (isModalVisible) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("keydown", handleMouseMove);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("keydown", handleMouseMove);
    };
  }, [isModalVisible]);

  return (
    <Modal
      title="Session Timeout Warning"
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      footer={null}
    >
      <p>
        Your session will expire in <strong>{remaining}</strong> seconds. Move
        your mouse or interact to stay logged in.
      </p>
    </Modal>
  );
};

export default IdleLogout;
